import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'react-toastify';

// ─── Syntax Highlighting ────────────────────────────────────────

const KEYWORDS = {
  javascript: new Set(['const', 'let', 'var', 'function', 'async', 'await', 'return', 'if', 'else', 'new', 'import', 'from', 'export', 'default', 'class', 'this', 'throw', 'try', 'catch', 'typeof', 'void', 'of', 'in', 'for', 'while']),
  python: new Set(['import', 'from', 'def', 'class', 'return', 'if', 'elif', 'else', 'for', 'while', 'in', 'not', 'and', 'or', 'is', 'with', 'as', 'try', 'except', 'raise', 'pass', 'print', 'str', 'int', 'float']),
  bash: new Set(['curl', 'if', 'then', 'else', 'fi', 'for', 'do', 'done', 'export', 'echo']),
  csharp: new Set(['using', 'var', 'new', 'if', 'else', 'await', 'async', 'return', 'class', 'public', 'private', 'static', 'void', 'string', 'int', 'bool', 'throw', 'try', 'catch', 'namespace']),
  php: new Set(['echo', 'if', 'else', 'elseif', 'function', 'return', 'class', 'public', 'private', 'new', 'use', 'namespace', 'array']),
};

const BUILTINS = new Set(['true', 'false', 'null', 'undefined', 'None', 'True', 'False', 'Guid', 'Console', 'JsonSerializer', 'JsonElement', 'HttpClient', 'StringContent', 'Encoding', 'System', 'Math', 'Dictionary']);

const escapeHtml = (str) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const highlightCode = (code, language) => {
  const tokens = [];
  let i = 0;
  const len = code.length;

  const hasSlashComment = ['javascript', 'csharp', 'php', 'json', 'html'].includes(language);
  const hasHashComment = ['python', 'bash', 'php'].includes(language);
  const hasBlockComment = ['javascript', 'csharp', 'php'].includes(language);
  const kw = KEYWORDS[language] || new Set();

  while (i < len) {
    // HTML comments
    if (language === 'html' && code.startsWith('<!--', i)) {
      const end = code.indexOf('-->', i + 4);
      const endIdx = end >= 0 ? end + 3 : len;
      tokens.push({ type: 'comment', start: i, end: endIdx });
      i = endIdx;
      continue;
    }

    // Block comments /* */
    if (hasBlockComment && code.startsWith('/*', i)) {
      const end = code.indexOf('*/', i + 2);
      const endIdx = end >= 0 ? end + 2 : len;
      tokens.push({ type: 'comment', start: i, end: endIdx });
      i = endIdx;
      continue;
    }

    // Single-line // comments
    if (hasSlashComment && code.startsWith('//', i)) {
      const end = code.indexOf('\n', i);
      const endIdx = end >= 0 ? end : len;
      tokens.push({ type: 'comment', start: i, end: endIdx });
      i = endIdx;
      continue;
    }

    // Hash comments
    if (hasHashComment && code[i] === '#') {
      const end = code.indexOf('\n', i);
      const endIdx = end >= 0 ? end : len;
      tokens.push({ type: 'comment', start: i, end: endIdx });
      i = endIdx;
      continue;
    }

    // PHP variable
    if (language === 'php' && code[i] === '$') {
      const m = code.slice(i).match(/^\$[a-zA-Z_]\w*/);
      if (m) {
        tokens.push({ type: 'variable', start: i, end: i + m[0].length });
        i += m[0].length;
        continue;
      }
    }

    // Strings (double and single quotes)
    if (code[i] === '"' || code[i] === "'") {
      const quote = code[i];
      let j = i + 1;
      while (j < len && code[j] !== quote) {
        if (code[j] === '\\') j++;
        j++;
      }
      if (j < len) j++;
      // Check if this is a JSON property key (string followed by colon or =>)
      const after = code.slice(j).match(/^\s*(?::(?!=)|=>)/);
      tokens.push({ type: after ? 'property' : 'string', start: i, end: j });
      i = j;
      continue;
    }

    // Template literals
    if (code[i] === '`' && language === 'javascript') {
      let j = i + 1;
      while (j < len && code[j] !== '`') {
        if (code[j] === '\\') j++;
        j++;
      }
      if (j < len) j++;
      tokens.push({ type: 'string', start: i, end: j });
      i = j;
      continue;
    }

    // Numbers
    if (/\d/.test(code[i]) && (i === 0 || /[^a-zA-Z_$]/.test(code[i - 1]))) {
      const m = code.slice(i).match(/^\d+\.?\d*/);
      if (m) {
        tokens.push({ type: 'number', start: i, end: i + m[0].length });
        i += m[0].length;
        continue;
      }
    }

    // HTML tags
    if (language === 'html' && code[i] === '<') {
      const m = code.slice(i).match(/^<\/?[a-zA-Z][\w-]*/);
      if (m) {
        tokens.push({ type: 'tag', start: i, end: i + m[0].length });
        i += m[0].length;
        continue;
      }
    }

    // Words (keywords, builtins, identifiers)
    if (/[a-zA-Z_$]/.test(code[i])) {
      const m = code.slice(i).match(/^[a-zA-Z_$][\w$]*/);
      if (m) {
        const word = m[0];
        if (kw.has(word)) {
          tokens.push({ type: 'keyword', start: i, end: i + word.length });
        } else if (BUILTINS.has(word)) {
          tokens.push({ type: 'builtin', start: i, end: i + word.length });
        }
        i += word.length;
        continue;
      }
    }

    i++;
  }

  // Build highlighted HTML
  let result = '';
  let pos = 0;
  for (const t of tokens) {
    if (t.start > pos) result += escapeHtml(code.slice(pos, t.start));
    result += `<span class="tk-${t.type}">${escapeHtml(code.slice(t.start, t.end))}</span>`;
    pos = t.end;
  }
  if (pos < len) result += escapeHtml(code.slice(pos));
  return result;
};

// ─── Styled Components ──────────────────────────────────────────

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const TabBar = styled.div`
  display: flex;
  overflow-x: auto;
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  gap: 0;
  &::-webkit-scrollbar { height: 3px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

const LanguageTab = styled.button`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  background: transparent;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => $active ? 600 : 400};
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const CodeWrapper = styled.div`
  position: relative;
`;

const CodePane = styled.pre`
  background: ${({ $isDark }) => $isDark ? '#1e2140' : '#fafbfc'};
  padding: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.lg};
  font-family: 'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace;
  font-size: 0.78rem;
  line-height: 1.65;
  max-height: 450px;
  overflow: auto;
  white-space: pre;
  color: ${({ $isDark }) => $isDark ? '#abb2bf' : '#24292e'};
  margin: 0;
  tab-size: 2;

  /* Light mode token colors */
  .tk-comment { color: ${({ $isDark }) => $isDark ? '#5c6370' : '#6a737d'}; font-style: italic; }
  .tk-string { color: ${({ $isDark }) => $isDark ? '#98c379' : '#067d17'}; }
  .tk-number { color: ${({ $isDark }) => $isDark ? '#d19a66' : '#005cc5'}; }
  .tk-keyword { color: ${({ $isDark }) => $isDark ? '#c678dd' : '#d73a49'}; }
  .tk-builtin { color: ${({ $isDark }) => $isDark ? '#56b6c2' : '#6f42c1'}; }
  .tk-property { color: ${({ $isDark }) => $isDark ? '#e06c75' : '#005cc5'}; }
  .tk-tag { color: ${({ $isDark }) => $isDark ? '#e06c75' : '#22863a'}; }
  .tk-variable { color: ${({ $isDark }) => $isDark ? '#e06c75' : '#e36209'}; }
`;

const CopyButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 500;
  transition: all 0.15s ease;
  z-index: 1;
  &:hover {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  margin: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  &:hover { background: ${({ theme }) => theme.colors.primary + '10'}; }
`;

// ─── Component ──────────────────────────────────────────────────

const CodeExampleViewer = ({ snippets = [], defaultTab }) => {
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  const [activeTab, setActiveTab] = useState(defaultTab || snippets[0]?.label || '');

  const activeSnippet = snippets.find(s => s.label === activeTab) || snippets[0];

  const highlighted = useMemo(() => {
    if (!activeSnippet) return '';
    return highlightCode(activeSnippet.code, activeSnippet.language);
  }, [activeSnippet]);

  const handleCopy = () => {
    if (!activeSnippet) return;
    navigator.clipboard.writeText(activeSnippet.code);
    toast.success('Copied to clipboard');
  };

  const handleDownloadPostman = () => {
    if (!activeSnippet) return;
    const blob = new Blob([activeSnippet.code], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inbound-webhook-collection.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!snippets.length) return null;

  return (
    <Container theme={theme}>
      {snippets.length > 1 && (
        <TabBar theme={theme}>
          {snippets.map(s => (
            <LanguageTab
              key={s.label}
              theme={theme}
              $active={activeTab === s.label}
              onClick={() => setActiveTab(s.label)}
            >
              {s.label}
            </LanguageTab>
          ))}
        </TabBar>
      )}
      <CodeWrapper>
        <CopyButton theme={theme} onClick={handleCopy}>Copy</CopyButton>
        <CodePane
          theme={theme}
          $isDark={isDark}
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      </CodeWrapper>
      {activeTab === 'Postman' && (
        <DownloadButton theme={theme} onClick={handleDownloadPostman}>
          Download Postman Collection
        </DownloadButton>
      )}
    </Container>
  );
};

export { CodeExampleViewer };
export default CodeExampleViewer;
